import { computed, ref, toValue, type MaybeRefOrGetter } from 'vue'

import { COMPARE_WINDOWS_BY_WINDOW } from '../models/alert.constants'
import type {
  AlertDraft,
  AlertMetricCondition,
  AlertRule,
  AlertWindowType,
  CompareWindow,
} from '../models/alert.types'
import type { ChannelAvailability } from '../models/channel.types'
import {
  useAlertFilterDropdownables,
  useAlertLevelMetadata,
  useAlertMetricCatalog,
  useAlertSelectableFields,
  type AlertReportColumn,
  type AlertReportLevel,
} from '../models/reports-bridge'
import { ruleSubscription, subscriptionFromFormValues } from '../service/alertFormValues'
import { alertDraftFromDto } from '../service/alertMapper'
import { alertService, type AlertSaveResult } from '../service/alertService'
import {
  AlertValidationError,
  validateAlertDraft,
  type AlertStepId,
} from '../service/alertValidator'
import { useAlertsStore } from '../stores/alertsStore'

import { useAccount } from '@/features/account'
import { ALERT_NOTIFICATION_TYPE_NAME } from '@/shared/config'
import { useI18n } from '@/shared/i18n'
import { useConfirm } from '@/shared/ui'

const STEP_IDS: readonly AlertStepId[] = ['monitor', 'filter', 'conditions', 'notify', 'review']
let attemptSequence = 0

export interface UseAlertWizardOptions {
  seed?: AlertRule
  /**
   * An Orpheus card's draft (`form_values`): over `seed` it fills an edit, alone a new alert. A draft
   * over a saved alert differs from it already, so "Update" saves even untouched.
   */
  formValues?: Record<string, unknown> | null
  readOnly?: boolean
  workspaceId: MaybeRefOrGetter<number | string | undefined>
  notificationTypeId: MaybeRefOrGetter<number | null | undefined>
  channelAvailability: MaybeRefOrGetter<readonly ChannelAvailability[]>
}

function cloneColumn(column: AlertReportColumn): AlertReportColumn {
  return { ...column, ...(column.meta ? { meta: { ...column.meta } } : {}) }
}

function cloneLevel(level: AlertReportLevel): AlertReportLevel {
  return {
    ...level,
    primary_fields: [...level.primary_fields],
    tableMetrics: level.tableMetrics.map(cloneColumn),
    tableDimensions: level.tableDimensions.map(cloneColumn),
  }
}

function snapshot(draft: AlertDraft): string {
  return JSON.stringify({
    id: draft.id ?? null,
    name: draft.name,
    level: draft.level?.partition_name ?? null,
    groupBy: draft.groupBy.map((column) => column.field).sort(),
    filters: draft.filters,
    window: draft.window,
    conditionJoin: draft.conditionJoin,
    customWindowDays: draft.customWindowDays,
    metrics: draft.metrics.map((condition) => ({
      field: condition.column.field,
      operator: condition.operator,
      mode: condition.mode,
      amount: condition.amount,
      compareWindow: condition.compareWindow,
    })),
    channels: [...draft.channels].sort(),
    recipientEmails: [...draft.recipientEmails].sort(),
    whatsappNumbers: [...draft.whatsappNumbers].sort(),
  })
}

function newAttemptKey(): string {
  attemptSequence += 1
  return `alert-wizard-${Date.now()}-${attemptSequence}`
}
/** Local draft state machine. It owns no HTTP and never mutates report-catalog objects. */
export function useAlertWizard(options: UseAlertWizardOptions) {
  const catalog = useAlertMetricCatalog()
  const levels = useAlertLevelMetadata()
  const selectableFields = useAlertSelectableFields()
  const account = useAccount()
  const store = useAlertsStore()
  const confirm = useConfirm()
  const { t } = useI18n()
  const selectableChannels = new Set(
    toValue(options.channelAvailability)
      .filter((channel) => channel.selectable)
      .map((channel) => channel.channel),
  )

  const workspaceIds = (): (number | string)[] =>
    [toValue(options.workspaceId)].filter((value): value is number | string => value !== undefined)

  const emptyDraft = (): AlertDraft => {
    const email = account.userEmail.value
    return {
      name: '',
      level: null,
      groupBy: [],
      filters: [],
      window: null,
      customWindowDays: null,
      metrics: [],
      conditionJoin: 'OR',
      channels: [],
      recipientEmails: email ? [email] : [],
      whatsappNumbers: [],
      readOnly: false,
    }
  }

  const seedDraft = (seed: AlertRule): AlertDraft => {
    const hydrated = alertDraftFromDto(ruleSubscription(seed), catalog, levels, {
      readOnly: options.readOnly ?? seed.triggerCount > 0,
      workspaceIds: workspaceIds(),
    })
    hydrated.channels = hydrated.channels.filter((channel) => selectableChannels.has(channel))
    return hydrated
  }

  const formValuesDraft = (values: Record<string, unknown>): AlertDraft => {
    const base = options.seed ? ruleSubscription(options.seed) : null
    const hydrated = alertDraftFromDto(subscriptionFromFormValues(values, base), catalog, levels, {
      readOnly: false,
      workspaceIds: workspaceIds(),
    })
    if (!options.seed) delete hydrated.id
    hydrated.channels = hydrated.channels.filter((channel) => selectableChannels.has(channel))
    const email = account.userEmail.value
    if (!hydrated.recipientEmails.length && !options.seed && email)
      hydrated.recipientEmails = [email]
    return hydrated
  }

  const createDraft = (): AlertDraft => {
    if (options.formValues) return formValuesDraft(options.formValues)
    return options.seed ? seedDraft(options.seed) : emptyDraft()
  }

  /** The alert as saved (or as a new alert starts) — what a draft over it is measured against. */
  const snapshotOfSeed = (): string =>
    snapshot(options.seed ? seedDraft(options.seed) : emptyDraft())

  const draft = ref<AlertDraft>(createDraft())
  // Legacy parity (`createNewAction`'s unconditional `await loadLevelFields()`): a seeded level
  // still needs its live field list — the static catalog clone above is only a placeholder.
  if (draft.value.level) void loadFieldsFor(draft.value.level.partition_name, false)
  // Legacy parity (old's `dropdownables`): "Is"/"Is not" filter values come from the report's
  // real distinct values, not free text — reactive to level changes since `report_id` moves
  // with it.
  const filterDropdownables = useAlertFilterDropdownables({
    reportId: () => draft.value.level?.report_id ?? 0,
    fields: () =>
      [
        ...(draft.value.level?.tableDimensions ?? []),
        ...(draft.value.level?.tableMetrics ?? []),
      ].map((column) => column.field),
    enabled: () => Boolean(draft.value.level),
  })
  // A draft over a saved alert is already a change: measure dirtiness against the alert as saved.
  const initialSnapshot = ref(options.formValues ? snapshotOfSeed() : snapshot(draft.value))
  const activeStep = ref<AlertStepId>('monitor')
  const saving = ref(false)
  const serverConditionError =
    ref<ReturnType<typeof validateAlertDraft>['stepErrors']['conditions']>(null)
  const attemptKey = newAttemptKey()
  const validation = computed(() => {
    const local = validateAlertDraft(draft.value, {
      channelAvailability: toValue(options.channelAvailability),
    })
    if (!serverConditionError.value || local.stepErrors.conditions) return local
    return {
      ...local,
      ok: false,
      stepErrors: { ...local.stepErrors, conditions: serverConditionError.value },
      firstInvalidStep: local.firstInvalidStep ?? ('conditions' as const),
    }
  })
  const stepErrors = computed(() => validation.value.stepErrors)
  const stepErrorKeys = computed(() => validation.value.stepErrorKeys)
  const canNext = computed(() => stepErrors.value[activeStep.value] === null)
  const isDirty = computed(() => snapshot(draft.value) !== initialSnapshot.value)
  function updateDraft(patch: Partial<AlertDraft>): void {
    serverConditionError.value = null
    draft.value = { ...draft.value, ...patch }
  }
  /**
   * Legacy parity (`changeLevel`/`loadLevelFields`): the static catalog clone on `draft.level` is
   * only a placeholder — the live, workspace-scoped field list is the authoritative source for
   * "Group by" and "Track these metrics". `prune` mirrors old's own asymmetry: a level *change*
   * drops selections the new field list no longer offers; the initial hydration of an existing
   * alert never does — "an alert survives a level whose field list came back empty."
   *
   * Patches `draft.value` directly rather than through `updateDraft` — this is a background sync,
   * not a user edit, and must not clear a `serverConditionError` a concurrent `save()` just set.
   */
  async function loadFieldsFor(partitionName: string, prune: boolean): Promise<void> {
    const { dimensions, metrics } = await selectableFields.load(partitionName)
    // Discard a stale response — the user picked a different level before this resolved.
    if (draft.value.level?.partition_name !== partitionName) return
    const patch: Partial<AlertDraft> = {
      level: { ...draft.value.level, tableDimensions: dimensions, tableMetrics: metrics },
    }
    if (prune) {
      const dimensionColumns = new Map(dimensions.map((column) => [column.field, column]))
      const metricColumns = new Map(metrics.map((column) => [column.field, column]))
      patch.groupBy = draft.value.groupBy.flatMap((column) => {
        const match = dimensionColumns.get(column.field)
        return match ? [cloneColumn(match)] : []
      })
      patch.metrics = draft.value.metrics.flatMap((condition) => {
        const match = metricColumns.get(condition.column.field)
        return match ? [{ ...condition, column: cloneColumn(match) }] : []
      })
    }
    draft.value = { ...draft.value, ...patch }
  }

  function setLevel(level: AlertReportLevel | null): void {
    serverConditionError.value = null
    const next = level ? cloneLevel(level) : null
    updateDraft({ level: next })
    if (next) void loadFieldsFor(next.partition_name, true)
  }

  function setGroupBy(fields: readonly string[]): void {
    serverConditionError.value = null
    const columns = new Map(draft.value.level?.tableDimensions.map((item) => [item.field, item]))
    updateDraft({
      groupBy: fields.flatMap((field) =>
        columns.get(field) ? [cloneColumn(columns.get(field)!)] : [],
      ),
    })
  }

  function setMetricFields(fields: readonly string[]): void {
    serverConditionError.value = null
    const columns = new Map(draft.value.level?.tableMetrics.map((item) => [item.field, item]))
    const current = new Map(draft.value.metrics.map((item) => [item.column.field, item]))
    updateDraft({
      metrics: fields.flatMap((field) => {
        const column = columns.get(field)
        if (!column) return []
        const existing = current.get(field)
        return [
          existing
            ? { ...existing, column: cloneColumn(column) }
            : {
                column: cloneColumn(column),
                operator: '<',
                mode: 'value',
                amount: null,
                compareWindow: null,
              },
        ]
      }),
    })
  }

  function setConditionJoin(operator: 'AND' | 'OR'): void {
    serverConditionError.value = null
    updateDraft({ conditionJoin: operator })
  }

  function setWindow(window: AlertWindowType | null): void {
    serverConditionError.value = null
    const allowed: readonly CompareWindow[] = window ? COMPARE_WINDOWS_BY_WINDOW[window] : []
    updateDraft({
      window,
      customWindowDays: window === 'custom' ? draft.value.customWindowDays : null,
      metrics: draft.value.metrics.map((condition) => ({
        ...condition,
        compareWindow:
          condition.mode === 'percentage'
            ? allowed.includes(condition.compareWindow!)
              ? condition.compareWindow
              : (allowed[0] ?? null)
            : null,
      })),
    })
  }

  function updateCondition(field: string, patch: Partial<AlertMetricCondition>): void {
    serverConditionError.value = null
    draft.value.metrics = draft.value.metrics.map((condition) => {
      if (condition.column.field !== field) return condition
      const updated = { ...condition, ...patch }
      if (updated.mode === 'value') updated.compareWindow = null
      if (updated.mode === 'percentage' && draft.value.window) {
        const allowed: readonly CompareWindow[] = COMPARE_WINDOWS_BY_WINDOW[draft.value.window]
        if (!updated.compareWindow || !allowed.includes(updated.compareWindow)) {
          updated.compareWindow = allowed[0] ?? null
        }
      }
      return updated
    })
  }

  function goTo(step: AlertStepId): void {
    activeStep.value = step
  }

  function next(): boolean {
    if (!canNext.value) return false
    const index = STEP_IDS.indexOf(activeStep.value)
    const nextStep = STEP_IDS[index + 1]
    if (nextStep) activeStep.value = nextStep
    return true
  }

  function back(): void {
    const index = STEP_IDS.indexOf(activeStep.value)
    const previous = STEP_IDS[index - 1]
    if (previous) activeStep.value = previous
  }

  async function save(): Promise<AlertSaveResult> {
    const result = validation.value
    if (!result.ok) {
      if (result.firstInvalidStep) goTo(result.firstInvalidStep)
      throw new AlertValidationError(result)
    }
    const workspaceId = toValue(options.workspaceId)
    const notificationTypeId = toValue(options.notificationTypeId)
    if (workspaceId === undefined || notificationTypeId == null) {
      throw new Error(`${ALERT_NOTIFICATION_TYPE_NAME} save context is unavailable`)
    }
    saving.value = true
    try {
      const saved = await alertService.save(draft.value, {
        workspaceId,
        notificationTypeId,
        idempotencyKey: attemptKey,
        channelAvailability: toValue(options.channelAvailability),
      })
      if (saved.fieldError) {
        serverConditionError.value = saved.fieldError.key
        goTo('conditions')
      }
      if (saved.ok) {
        await store.upsertFromSave()
        initialSnapshot.value = snapshot(draft.value)
      }
      return saved
    } finally {
      saving.value = false
    }
  }
  async function requestClose(): Promise<boolean> {
    if (!isDirty.value) return true
    return confirm({
      title: t('alerts.discard.title'),
      message: t('alerts.discard.body'),
      confirmLabel: t('alerts.discard.confirm'),
      cancelLabel: t('common.cancel'),
      danger: true,
    })
  }
  return {
    draft,
    activeStep,
    steps: STEP_IDS,
    stepErrors,
    stepErrorKeys,
    canNext,
    isDirty,
    saving,
    levels: levels.levels,
    fieldsLoading: selectableFields.isLoading,
    filterDropdownables: filterDropdownables.dropdownables,
    loadFilterDropdownValues: filterDropdownables.loadDropdownValues,
    updateDraft,
    setLevel,
    setGroupBy,
    setMetricFields,
    setConditionJoin,
    setWindow,
    updateCondition,
    next,
    back,
    goTo,
    save,
    requestClose,
  }
}
